import { SELECTION_STROKE_COLOR } from "@gettactic/map-builder/src/components/map/defines";
import type { Shape } from "@gettactic/map-builder/src/types";
import React from "react";
import { useShallow } from "zustand/react/shallow";
import {
  getFrameBox,
  getShapeLocalBox,
  shapeFrameTransform
} from "../document/bounds";
import { useEditorSelector } from "../store/EditorStoreContext";
import { useViewport } from "../store/selectors";
import type { Rect } from "../types";
import { HoverOutline } from "./HoverOutline";
import {
  HANDLE_CURSORS,
  isResizable,
  isRotatable,
  RESIZE_HANDLES
} from "./handles";
import {
  type Readout,
  type SnapGuides,
  useOverlayValue
} from "./overlayChannel";
import { ShapeHandlesSvg } from "./ShapeHandlesSvg";
import { TextEditorOverlay } from "./TextEditorOverlay";

const HANDLE_SIZE = 8;
const ROTATE_OFFSET = 24;
const MARQUEE_FILL = "rgba(0, 102, 255, 0.1)";
const MARQUEE_STROKE = "#0066ff";

const useSelectedShapes = (): Shape[] =>
  useEditorSelector(
    useShallow((state) =>
      state.selection
        .map((id) => state.shapes[id])
        .filter((shape): shape is Shape => !!shape && !shape.hidden)
    )
  );

/**
 * Selection outlines, transform handles, marquee and snap guides. Subscribes
 * to the selection and the selected shapes only; during a drag the tool
 * moves the whole group through the overlay channel instead of the store.
 */
export const OverlayLayer: React.FC = () => {
  const selected = useSelectedShapes();
  const { scale } = useViewport();
  const marquee = useOverlayValue((state) => state.marquee);
  const guides = useOverlayValue((state) => state.guides);
  const selectionTransform = useOverlayValue(
    (state) => state.selectionTransform
  );
  const readout = useOverlayValue((state) => state.readout);
  const handleSize = HANDLE_SIZE / scale;
  const single = selected.length === 1 ? selected[0] : null;

  return (
    <g data-layer="overlay" pointerEvents="none">
      <HoverOutline />
      <g transform={selectionTransform ?? undefined}>
        {selected.map((shape) => {
          // The outline follows the shape's own frame, so a rotated shape
          // shows a rotated box (and the handles below stay attached to it).
          const box = getShapeLocalBox(shape);
          return (
            <rect
              key={shape.id}
              x={box.x}
              y={box.y}
              width={box.width}
              height={box.height}
              transform={shapeFrameTransform(shape)}
              fill="none"
              stroke={SELECTION_STROKE_COLOR}
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
              strokeDasharray={shape.locked ? "4 3" : undefined}
            />
          );
        })}
        {single && !single.locked ? (
          <>
            <g transform={shapeFrameTransform(single)}>
              <Handles
                shape={single}
                box={getShapeLocalBox(single)}
                size={handleSize}
              />
            </g>
            <ShapeHandlesSvg shape={single} radius={handleSize * 0.65} />
          </>
        ) : null}
        {selected.length > 1 ? (
          <GroupHandles shapes={selected} size={handleSize} />
        ) : null}
      </g>
      <TextEditorOverlay />
      {guides ? <SnapGuideLines guides={guides} /> : null}
      {readout ? <ReadoutLabel readout={readout} scale={scale} /> : null}
      {marquee ? (
        <rect
          x={marquee.x}
          y={marquee.y}
          width={marquee.width}
          height={marquee.height}
          fill={MARQUEE_FILL}
          stroke={MARQUEE_STROKE}
          strokeWidth={1}
          vectorEffect="non-scaling-stroke"
        />
      ) : null}
    </g>
  );
};

interface HandlesProps {
  shape: Shape;
  box: Rect;
  size: number;
}

/** Handles are hit-tested by `data-handle`; they only show for shapes the
 *  select tool knows how to transform. Positions are in the shape's own
 *  frame; the caller rotates the group. */
const Handles: React.FC<HandlesProps> = ({ shape, box, size }) => {
  const canResize = isResizable(shape);
  const canRotate = isRotatable(shape);
  const half = size / 2;
  const positions: Record<string, { x: number; y: number }> = {
    nw: { x: box.x, y: box.y },
    n: { x: box.x + box.width / 2, y: box.y },
    ne: { x: box.x + box.width, y: box.y },
    e: { x: box.x + box.width, y: box.y + box.height / 2 },
    se: { x: box.x + box.width, y: box.y + box.height },
    s: { x: box.x + box.width / 2, y: box.y + box.height },
    sw: { x: box.x, y: box.y + box.height },
    w: { x: box.x, y: box.y + box.height / 2 }
  };
  return (
    <g pointerEvents="all">
      {canResize
        ? RESIZE_HANDLES.map((handle) => (
            <rect
              key={handle}
              data-handle={handle}
              x={positions[handle].x - half}
              y={positions[handle].y - half}
              width={size}
              height={size}
              fill="white"
              stroke={SELECTION_STROKE_COLOR}
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
              style={{ cursor: HANDLE_CURSORS[handle] }}
            />
          ))
        : null}
      {canRotate ? (
        <>
          <line
            x1={box.x + box.width / 2}
            y1={box.y}
            x2={box.x + box.width / 2}
            y2={box.y - ROTATE_OFFSET * (size / HANDLE_SIZE)}
            stroke={SELECTION_STROKE_COLOR}
            strokeWidth={1}
            vectorEffect="non-scaling-stroke"
          />
          <circle
            data-handle="rotate"
            cx={box.x + box.width / 2}
            cy={box.y - ROTATE_OFFSET * (size / HANDLE_SIZE)}
            r={half}
            fill="white"
            stroke={SELECTION_STROKE_COLOR}
            strokeWidth={1}
            vectorEffect="non-scaling-stroke"
            style={{ cursor: HANDLE_CURSORS.rotate }}
          />
        </>
      ) : null}
    </g>
  );
};

/** Resize and rotate handles around a multi-selection's overall box, in
 *  the frame the selection has been rotated into. */
const GroupHandles: React.FC<{ shapes: Shape[]; size: number }> = ({
  shapes,
  size
}) => {
  const frameRotation = useEditorSelector(
    (state) => state.chrome.selectionFrameRotation
  );
  const movable = shapes.filter((shape) => !shape.locked);
  const frame = getFrameBox(movable, frameRotation);
  if (!frame || movable.length < 2) return null;
  return (
    <g transform={frame.transform}>
      <rect
        x={frame.box.x}
        y={frame.box.y}
        width={frame.box.width}
        height={frame.box.height}
        fill="none"
        stroke={SELECTION_STROKE_COLOR}
        strokeWidth={1}
        strokeDasharray="6 4"
        vectorEffect="non-scaling-stroke"
      />
      <Handles shape={GROUP_HANDLE_SHAPE} box={frame.box} size={size} />
    </g>
  );
};

/** A stand-in that makes `Handles` show both resize and rotate handles. */
const GROUP_HANDLE_SHAPE: Shape = {
  id: "selection",
  type: "rectangle",
  x: 0,
  y: 0,
  width: 0,
  height: 0,
  layerType: "shapes"
};

const GUIDE_EXTENT = 100000;
const SNAP_COLOR = "#FF4D94";
const CENTER_COLOR = "#10b981";
const CENTER_PLUS = 10;
const DISTANCE_TICK = 6;

/** Snap feedback in the Konva editor's colours: pink alignment lines with
 *  a dot on the target; a green outline, crosshair and plus when the shape
 *  is centred inside another; pink dimension lines with the gap when Alt
 *  is held. */
const SnapGuideLines: React.FC<{ guides: SnapGuides }> = ({ guides }) => {
  const centred =
    guides.centeredIn && guides.x !== undefined && guides.y !== undefined
      ? guides.centeredIn
      : null;
  return (
    <g>
      {centred && guides.x !== undefined && guides.y !== undefined ? (
        <CentredCue
          container={centred}
          active={guides.activeBounds}
          cx={guides.x}
          cy={guides.y}
        />
      ) : (
        <>
          {guides.x !== undefined ? (
            <line
              x1={guides.x}
              y1={-GUIDE_EXTENT}
              x2={guides.x}
              y2={GUIDE_EXTENT}
              stroke={SNAP_COLOR}
              strokeWidth={1}
              strokeDasharray="6 4"
              vectorEffect="non-scaling-stroke"
            />
          ) : null}
          {guides.y !== undefined ? (
            <line
              x1={-GUIDE_EXTENT}
              y1={guides.y}
              x2={GUIDE_EXTENT}
              y2={guides.y}
              stroke={SNAP_COLOR}
              strokeWidth={1}
              strokeDasharray="6 4"
              vectorEffect="non-scaling-stroke"
            />
          ) : null}
          {guides.x !== undefined && guides.targetDot?.y !== undefined ? (
            <circle
              cx={guides.x}
              cy={guides.targetDot.y}
              r={4}
              fill={SNAP_COLOR}
            />
          ) : null}
          {guides.y !== undefined && guides.targetDot?.x !== undefined ? (
            <circle
              cx={guides.targetDot.x}
              cy={guides.y}
              r={4}
              fill={SNAP_COLOR}
            />
          ) : null}
        </>
      )}
      {guides.distances?.map((distance) => (
        <DistanceLine
          key={`${distance.axis}-${distance.from.x}-${distance.from.y}`}
          distance={distance}
        />
      ))}
    </g>
  );
};

const CentredCue: React.FC<{
  container: NonNullable<SnapGuides["centeredIn"]>;
  active: SnapGuides["activeBounds"];
  cx: number;
  cy: number;
}> = ({ container, active, cx, cy }) => (
  <g>
    <rect
      x={container.left}
      y={container.top}
      width={container.right - container.left}
      height={container.bottom - container.top}
      fill="none"
      stroke={CENTER_COLOR}
      strokeWidth={1.5}
      strokeDasharray="10 6"
      vectorEffect="non-scaling-stroke"
    />
    {active ? (
      <rect
        x={active.left}
        y={active.top}
        width={active.right - active.left}
        height={active.bottom - active.top}
        fill="none"
        stroke={CENTER_COLOR}
        strokeWidth={1.5}
        opacity={0.9}
        vectorEffect="non-scaling-stroke"
      />
    ) : null}
    <line
      x1={cx}
      y1={Math.min(container.top, active?.top ?? container.top)}
      x2={cx}
      y2={Math.max(container.bottom, active?.bottom ?? container.bottom)}
      stroke={CENTER_COLOR}
      strokeWidth={2}
      strokeDasharray="10 6"
      vectorEffect="non-scaling-stroke"
    />
    <line
      x1={Math.min(container.left, active?.left ?? container.left)}
      y1={cy}
      x2={Math.max(container.right, active?.right ?? container.right)}
      y2={cy}
      stroke={CENTER_COLOR}
      strokeWidth={2}
      strokeDasharray="10 6"
      vectorEffect="non-scaling-stroke"
    />
    <line
      x1={cx - CENTER_PLUS}
      y1={cy}
      x2={cx + CENTER_PLUS}
      y2={cy}
      stroke={CENTER_COLOR}
      strokeWidth={2}
      vectorEffect="non-scaling-stroke"
    />
    <line
      x1={cx}
      y1={cy - CENTER_PLUS}
      x2={cx}
      y2={cy + CENTER_PLUS}
      stroke={CENTER_COLOR}
      strokeWidth={2}
      vectorEffect="non-scaling-stroke"
    />
    <circle cx={cx} cy={cy} r={3} fill={CENTER_COLOR} />
  </g>
);

const DistanceLine: React.FC<{
  distance: NonNullable<SnapGuides["distances"]>[number];
}> = ({ distance }) => {
  const isVertical = distance.axis === "vertical";
  const midX = (distance.from.x + distance.to.x) / 2;
  const midY = (distance.from.y + distance.to.y) / 2;
  const tick = (point: { x: number; y: number }) =>
    isVertical
      ? {
          x1: point.x - DISTANCE_TICK,
          y1: point.y,
          x2: point.x + DISTANCE_TICK,
          y2: point.y
        }
      : {
          x1: point.x,
          y1: point.y - DISTANCE_TICK,
          x2: point.x,
          y2: point.y + DISTANCE_TICK
        };
  return (
    <g>
      <line
        x1={distance.from.x}
        y1={distance.from.y}
        x2={distance.to.x}
        y2={distance.to.y}
        stroke={SNAP_COLOR}
        strokeWidth={1}
        vectorEffect="non-scaling-stroke"
      />
      <line
        {...tick(distance.from)}
        stroke={SNAP_COLOR}
        strokeWidth={1}
        vectorEffect="non-scaling-stroke"
      />
      <line
        {...tick(distance.to)}
        stroke={SNAP_COLOR}
        strokeWidth={1}
        vectorEffect="non-scaling-stroke"
      />
      <text
        x={isVertical ? midX + 10 : midX}
        y={isVertical ? midY : midY - 10}
        textAnchor={isVertical ? "start" : "middle"}
        dominantBaseline="central"
        fill={SNAP_COLOR}
        fontSize={12}
        fontFamily="Quicksand"
        fontWeight={700}
      >
        {Math.round(distance.value)}
      </text>
    </g>
  );
};

const READOUT_OFFSET = 16;
const READOUT_LINE_HEIGHT = 15;
const READOUT_CHAR_WIDTH = 7.2;

/** Gesture numbers in a small tag beside the pointer, screen sized. */
const ReadoutLabel: React.FC<{ readout: Readout; scale: number }> = ({
  readout,
  scale
}) => {
  const longest = Math.max(...readout.lines.map((line) => line.length));
  const width = longest * READOUT_CHAR_WIDTH + 12;
  const height = readout.lines.length * READOUT_LINE_HEIGHT + 6;
  return (
    <g
      data-readout
      transform={`translate(${readout.anchor.x} ${readout.anchor.y}) scale(${1 / scale})`}
    >
      <rect
        x={READOUT_OFFSET}
        y={READOUT_OFFSET}
        width={width}
        height={height}
        rx={4}
        fill="rgba(23, 23, 23, 0.85)"
      />
      {readout.lines.map((line, index) => (
        <text
          key={line}
          x={READOUT_OFFSET + 6}
          y={READOUT_OFFSET + 3 + READOUT_LINE_HEIGHT * (index + 0.5)}
          dominantBaseline="central"
          fill="white"
          fontSize={12}
          fontFamily="Quicksand"
          fontWeight={700}
          style={{ fontVariantNumeric: "tabular-nums" }}
        >
          {line}
        </text>
      ))}
    </g>
  );
};
